import {
  formatChartParamsDate,
  listTimeTitleFunc,
} from '@op/components/ChartCommon/formatter';
import EChartCommon, { getAxisPointer } from '@op/components/EChartCommon';
import { NO_DATE } from '@op/constants';
import { getChartsShowCount } from '@op/utils';
import { formatTimeXAxisText, getChartsGradientColor } from '@op/utils/chart';
import { reviewsFormat } from '@op/utils/value';
import React, { useMemo } from 'react';
import { useIntl } from 'umi';
import { dealwithTimeline, stackedLineChart } from '../utils';
import { momentTimeAAfterB } from '@op/utils/time';
import { DBO_TM } from '@op/utils/format';

export default function SteamStackedTimeline({
  oldValue,
  newValue,
  filter,
  leftTitle,
  list,
  height,
  icon,
}: any) {
  const { formatMessage } = useIntl();

  const valueList = stackedLineChart(newValue);
  const oldList = stackedLineChart(oldValue);
  const { oldYAxis, yAxis, xAxis, timeMapValue } = dealwithTimeline(
    valueList,
    oldList,
    'review',
  );

  const isEmpty = useMemo(() => {
    const list = [...oldYAxis, ...yAxis];
    return !list.some((item) => item);
  }, [oldYAxis, yAxis]);

  // 设置图形配置
  const makeupOptions = () => {
    const utcMinusTime = DBO_TM.utc().format('YYYY-MM-DD HH:mm:ss');
    const seriesData: Array<any> = [];
    const legendData: Array<any> = [];


    list.forEach(({ color, name }: any, index: number) => {
      legendData.push({
        name: name,
        itemStyle: { color },
      });
      const isCurrent = index === 0;
      const item = {
        name: name,
        type: 'line' as any,
        connectNulls: true,
        color,
        stack: 'total' + index,
        areaStyle: getChartsGradientColor(color, 0, isCurrent ? 0.5 : 0.4),
        data: isCurrent ? yAxis : oldYAxis,
        itemStyle: { color },
        lineStyle: {
          type: isCurrent ? 'solid' : 'dashed',
        },
        endLabel: {
          show: isCurrent,
          offset: [-20, -10],
          formatter: (params: any) => {
            const value = params.value;
            return reviewsFormat(value);
          },
        },
      };
      if (isCurrent) {
        item.data = item.data.map((item: number, index: number) => {
          const currentTime = xAxis[index];
          if (momentTimeAAfterB(utcMinusTime, currentTime)) {
            return item;
          }

          return null;
        });
      }
      seriesData.push(item);
    });

    seriesData.reverse();

    const opts = {
      tooltip: {
        position: (
          pos: [any, any],
          params: any,
          dom: any,
          rect: any,
          size: { viewSize: [any, any] },
        ) => {
          // 鼠标在左侧时 tooltip 显示到右侧，鼠标在右侧时 tooltip 显示到左侧。
          const [canvasWidth, canvasHeight] = size.viewSize;
          const [mouseX, mouseY] = pos;
          if (mouseX < canvasWidth / 2) {
            return [mouseX + 10, mouseY / 6];
          }
          return [mouseX - 240, mouseY / 6];
        },
        formatter: (params: any) => {
          const time = params?.[0]?.axisValue;
          let title = listTimeTitleFunc(time, filter.date_type, true);
          const otherTitle = listTimeTitleFunc(
            timeMapValue[time],
            filter.date_type,
            true,
          );

          return formatChartParamsDate({
            params,
            listTitleFunc: (params) => {
              const name = params.seriesName;
              return `<span>${!icon
                ? ''
                : `<img src=${icon} style="width: 18px;height: 18px; vertical-align: middle;margin-right:4px" />`
                }${formatMessage({
                  id: name || NO_DATE.EMPTY,
                })}</span>`;
            },
            valueFormat: (value) => {
              return reviewsFormat(value);
            },
            addExtraInfo: (element: any) => {
              return { name: element.seriesName };
            },
            tablesConfigFunc: (records: Array<any>) => {
              const periodElement = records.find(
                (item) => item.name === list[0].name,
              );

              const beforeElement = records.find(
                (item) => item.name === list[1].name,
              );

              const columns = [];

              if (periodElement) {
                columns.push({
                  showHeader: true,
                  records: [periodElement],
                  columns: [
                    {
                      key: 'text',
                      header: title,
                    },
                    {
                      key: 'value',
                      align: 'right',
                      weight: 'bold',
                      header: formatMessage({ id: 'Mentions' }),
                    },
                  ],
                });
              }
              if (beforeElement) {
                columns.push({
                  showHeader: true,
                  records: [beforeElement],
                  columns: [
                    {
                      key: 'text',
                      header: otherTitle,
                    },
                    {
                      key: 'value',
                      align: 'right',
                      weight: 'bold',
                      header: ' ',
                    },
                  ],
                });
              }
              return columns;
            },
          });
        },
      },
      axisPointer: getAxisPointer((data) => {
        return formatTimeXAxisText(data.value, filter?.date_type);
      }),
      legend: {
        data: legendData,
        formatter: (name: any) => {
          const newName = formatMessage({ id: name || NO_DATE.EMPTY });
          if (icon) {
            return `{positive|} ${newName}`;
          }
          return newName;
        },
        textStyle: {
          color: '#888888',
          fontSize: 12,
          height: 14,
          lineHeight: 14,
          rich: {
            positive: {
              height: 18,
              width: 18,
              paddingRight: 4,
              backgroundColor: {
                image: icon,
              },
            },
          },
        },
      },
      xAxis: {
        data: xAxis,
        boundaryGap: false,
        axisLabel: {
          formatter: (value: string) =>
            formatTimeXAxisText(value, filter?.date_type),
        },
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: (value: number) => {
            const data = getChartsShowCount(Math.abs(value), 1);
            return value >= 0 ? data : `-${data}`;
          },
        },
      },
      grid: {
        top: 20,
        left: 20,
        right: 20,
        containLabel: true,
      },
      series: seriesData,
    };

    return opts;
  };

  return (
    <EChartCommon
      height={height}
      leftTitle={leftTitle}
      options={makeupOptions()}
      empty={isEmpty}
    />
  );
}
